import { goToPortal } from 'src/utils/routing';
import { serviceId } from 'src/constants/ServiceIdentifier';
import { useMfeOptions } from 'src/contexts/MfeOptionsContext';
import { Result } from 'src/types/result';
import { InitializationMethod } from 'src/types/mfeProps';
import { useShellRootContext } from 'src/contexts/ShellRoot';
import { useJweb } from './useJweb';
import { Logger } from 'src/utils/Logger';
import { closeModal } from 'src/utils/modal';
import { ScreenAction } from 'src/analytics/types';
import { publishEvent } from 'src/analytics/publishEvent';
import { formatResult } from 'src/analytics/utils';
import { triggerSplunkCustomEvent } from 'src/analytics/splunk-rum';
import {
  getGlobalExecutionState,
  setGlobalPredefinedExecutionData
} from 'src/utils/globalExecutionState';
export const useFinishExecution = () => {
  const {
    options: { initializationMethod }
  } = useMfeOptions();
  const {
    interfaces: { v1 }
  } = useShellRootContext();
  const { Plugins } = useJweb();

  const finishExecution = (resultData) => {
    const { predefinedExecutionData } = getGlobalExecutionState();

    // Use predefined data if available, otherwise use the provided resultData
    const dataToUse = predefinedExecutionData || resultData;

    Logger.log('Finishing execution with: ', dataToUse);
    Logger.log('with initialization method: ', initializationMethod);

    const navigation = v1.navigation;

    const isNavigation =
      initializationMethod === InitializationMethod.Navigation;
    const isJShellServiceRouting =
      initializationMethod === InitializationMethod.JShellServiceRouting;
    const isJWebServiceRouting =
      initializationMethod === InitializationMethod.JWebServiceRouting;
    const isModal = initializationMethod === InitializationMethod.Modal;

    const payload = {
      ...(dataToUse?.result && {
        screenMode: formatResult(dataToUse?.result)
      }),
      ...(dataToUse?.errorInfo?.errorCode && {
        actionAuxParams: `errorCode=${dataToUse?.errorInfo?.errorCode}`
      })
    };

    // Analytics - send event Simple UI
    publishEvent({ type: ScreenAction.COMPLETED, payload });
    // Analytics - send event Splunk RUM
    triggerSplunkCustomEvent(dataToUse?.result ?? Result.Failure);

    if (isNavigation) {
      const isWebPortal = v1.app?.getApplicationContext?.() === 'WEB_PORTAL';
      const isNativePortal =
        v1.app?.getApplicationContext?.() === 'NATIVE_PORTAL';
      const shouldRedirect = isWebPortal || isNativePortal;
      const shouldGoBack =
        dataToUse?.result === Result.Failure ||
        dataToUse?.result === Result.Declined;
      goToPortal({ navigation, shouldRedirect, shouldGoBack });
      return;
    }

    if (isJShellServiceRouting) {
      Logger.log('Closing service instance with JShell');
      Logger.log({ resultData: dataToUse });
      v1.serviceRouting.closeServiceInstance({ resultData: dataToUse });
      return;
    }

    if (isJWebServiceRouting) {
      Logger.log('Closing service instance with JWeb');
      Logger.log({ resultData: dataToUse });
      Plugins.ServiceRouting.closeServiceInstance({ resultData: dataToUse });
      return;
    }

    if (isModal) {
      Logger.log('Closing service instance with Modal');
      Logger.log({ resultData: dataToUse });
      closeModal();
      return;
    }
  };

  const setResultData = (result, errorObject) => {
    Logger.log('setResultData called with:', { result, errorObject });
    const resultData = {
      result,
      errorInfo: {
        errorCode: `${serviceId}.${errorObject?.errorCode}`,
        errorDescription: `${errorObject?.message}`
      }
    };
    Logger.log('Setting global predefinedExecutionData to:', resultData);
    setGlobalPredefinedExecutionData(resultData);

    const { predefinedExecutionData } = getGlobalExecutionState();
    Logger.debug(
      'Global predefinedExecutionData after set:',
      predefinedExecutionData
    );
  };

  const finishOnSuccess = (resourceId = '', result = Result.Success) => {
    const resultData = {
      result,
      output: {
        tenant: {
          resourceId
        }
      }
    };

    Logger.debug('Calling finishExecution with:', resultData);

    finishExecution(resultData);
  };

  const finishOnError = (result = Result.Failure, errorObject) => {
    const resultData = {
      result,
      errorInfo: {
        errorCode: `${serviceId}.${errorObject?.errorCode}`,
        errorDescription: `${errorObject?.message}`
      }
    };

    Logger.debug('Calling finishExecution with:', resultData);

    finishExecution(resultData);
  };

  return { finishOnSuccess, finishOnError, setResultData };
};
